import { CdkDragHandle } from '@angular/cdk/drag-drop';
import {
    ElementRef,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    input,
    output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { NgIcon } from '@ng-icons/core';
import { QpxFormFieldDirectives } from '@quipex/shared/ui';
import {
    DialogService,
    QpxButton,
    QpxIcon,
    toDialogConfirmation,
} from '@quipex/shared/ui';
import { take } from 'rxjs';
import { FocusRestoration } from '../../focus-restoration';
import type { PackDraftRequirement, PackDraftSection } from '../../models';
import { ProposalState } from '../../proposal-state';
@Component({
    selector: 'qpx-quippy-pack-requirement-heading',
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        QpxFormFieldDirectives,
        CdkDragHandle,
        MatButtonModule,
        MatMenuModule,
        NgIcon,
        QpxButton,
        QpxIcon,
    ],
    templateUrl: './requirement-heading.html',
    styleUrl: '../pack-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.small]': 'smallHeaders()',
        class: 'requirement-heading block min-w-0',
    },
})
export class QuippyPackRequirementHeading {
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(DialogService);
    private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly focus = inject(FocusRestoration);
    protected readonly draft = inject(ProposalState);
    public readonly smallHeaders = input(false);
    public readonly actionId = input.required<string>();
    public readonly row = input.required<PackDraftRequirement>();
    public readonly group = input.required<PackDraftSection>();
    public readonly rowIndex = input.required<number>();
    public readonly disabled = input(false);
    public readonly changed = output<void>();
    public readonly moved = output<string>();
    protected remove(): void {
        if (this.disabled()) return;
        const row = this.row();
        const group = this.group();
        const actionId = this.actionId();
        const documentCount = row.documents.length;
        const sectionName = this.element.nativeElement
            .closest('section')
            ?.querySelector<HTMLInputElement>(
                'input[aria-label="Section name"]'
            );
        this.dialog
            .openConfirmationDialog(
                {
                    title: 'Delete requirement',
                    text1: `Delete “${row.name.trim() || 'requirement'}” from this draft? ${documentCount} ${documentCount === 1 ? 'document remains' : 'documents remain'} available to assign elsewhere.`,
                    confirmButton: 'Delete requirement',
                    cancelButton: 'Cancel',
                    status: 'destructive',
                },
                { restoreFocus: true }
            )
            .afterClosed()
            .pipe(
                toDialogConfirmation(),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((confirmed) => {
                if (
                    !confirmed ||
                    this.disabled() ||
                    this.actionId() !== actionId ||
                    !this.draft.groups().includes(group) ||
                    !group.rows.includes(row)
                )
                    return;
                this.draft.removeRow(row.id);
                this.changed.emit();
                this.focus.afterRender(sectionName?.id);
            });
    }

    protected moveTo(key: string, index: number): void {
        if (this.disabled()) return;
        const id = `${this.actionId()}-requirement-${this.row().id}`;
        this.draft.moveRow(this.row().id, key, index);
        this.moved.emit(key);
        this.changed.emit();
        this.focus.afterRender(id);
    }
}
